import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import API_URL from './config';
import { fetchCardByName } from './utils/scryfallApi';
import { sanitizeCommanderName, sanitizeThemeName } from './utils/sanitizeName';
import Header from './components/Header';
import CommanderInput from './components/CommanderInput';
import ThemeList from './components/ThemeList';
import Footer from './components/Footer';
import PageTransition from './components/PageTransition';
import PartnerSelector, { getPartnerType } from './components/PartnerSelector';
import './styles/App.css';

// Lazy load components not needed on initial home page render
const CardSelector = lazy(() => import('./components/CardSelector'));
const FAQ = lazy(() => import('./components/FAQ'));
const MyDecks = lazy(() => import('./components/MyDecks'));
const SavedDeckViewer = lazy(() => import('./components/SavedDeckViewer'));
const ManabaseGenerator = lazy(() => import('./components/ManabaseGenerator'));

// Loading fallback
const LoadingSpinner = () => (
  <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
    <div className="loading-spinner"></div>
  </div>
);

// Underplayed commanders pool (bottom 100 from EDHREC rankings)
const UNDERPLAYED_COMMANDERS = [
  'Abaddon the Despoiler', 'Karametra, God of Harvests', 'Slimefoot, the Stowaway',
  'Jinnie Fay, Jetmir\'s Second', 'Neriv, Heart of the Storm', 'Aragorn, King of Gondor',
  'Kyler, Sigardian Emissary', 'Heliod, the Radiant Dawn', 'Marisi, Breaker of the Coil',
  'Gisa, the Hellraiser', 'Kozilek, the Great Distortion', 'Emry, Lurker of the Loch',
  'Zur, Eternal Schemer', 'Krark, the Thumbless', 'Karn, Legacy Reforged',
  'Rakdos, the Muscle', 'Hapatra, Vizier of Poisons', 'Syr Konrad, the Grim',
  'Mizzix of the Izmagnus', 'Galea, Kindler of Hope', 'Baba Lysaga, Night Witch',
  'Doran, the Siege Tower', 'Karlov of the Ghost Council', 'Lumra, Bellow of the Woods',
  'Loot, the Key to Everything', 'Zurgo Helmsmasher', 'Trostani, Selesnya\'s Voice',
  'Amalia Benavides Aguirre', 'Imoti, Celebrant of Bounty', 'Runo Stromkirk',
  'Anje Falkenrath', 'Jorn, God of Winter', 'Solphim, Mayhem Dominus',
  'Otharri, Suns\' Glory', 'Raphael, Fiendish Savior', 'Uril, the Miststalker',
  'Riku of Two Reflections', 'Zask, Skittering Swarmlord', 'Rhys the Redeemed',
  'Neera, Wild Mage', 'Jarad, Golgari Lich Lord', 'Osgir, the Reconstructor',
  'Elminster', 'Kumena, Tyrant of Orazca', 'Hamza, Guardian of Arashin',
  'Yennett, Cryptic Sovereign', 'Progenitus', 'Arixmethes, Slumbering Isle',
  'Garth One-Eye', 'Ashling, Flame Dancer', 'Jan Jansen, Chaos Crafter',
  'Yargle and Multani', 'Jared Carthalion', 'Admiral Beckett Brass',
  'Elsha of the Infinite', 'Agatha of the Vile Cauldron', 'Baral, Chief of Compliance',
  'Atarka, World Render', 'Goreclaw, Terror of Qal Sisma', 'Imskir Iron-Eater',
  'Eriette, the Beguiler', 'Lonis, Cryptozoologist', 'Minthara, Merciless Soul',
  'Kenessos, Priest of Thassa', 'Asmoranomardicadaistinaculdacar', 'Yusri, Fortune\'s Flame',
  'Ghired, Mirror of the Wilds', 'Aurelia, the Warleader'
];

// Fisher-Yates shuffle to get random commanders
const getRandomCommanders = (pool, count) => {
  const shuffled = [...pool].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count).map(name => ({ name, display: name }));
};

// Cache for popular commander card data (persists across page navigations)
const popularCommanderCache = {};

// Popular Commander Card Component
const PopularCommanderCard = ({ commander, onClick }) => {
  const [imageUrl, setImageUrl] = useState(popularCommanderCache[commander.name]?.imageUrl || '');
  const [colors, setColors] = useState(popularCommanderCache[commander.name]?.colors || []);
  const [isLoading, setIsLoading] = useState(!popularCommanderCache[commander.name]);

  useEffect(() => {
    // Skip fetch if already cached
    if (popularCommanderCache[commander.name]) {
      return;
    }
    
    const fetchCardData = async () => {
      try {
        const data = await fetchCardByName(commander.name);
        const fetchedImageUrl = data.image_uris?.art_crop || data.card_faces?.[0]?.image_uris?.art_crop || '';
        const fetchedColors = data.color_identity || [];
        
        // Cache the result
        popularCommanderCache[commander.name] = {
          imageUrl: fetchedImageUrl,
          colors: fetchedColors
        };
        
        setImageUrl(fetchedImageUrl);
        setColors(fetchedColors);
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching commander:', commander.name, error);
        setIsLoading(false);
      }
    };
    fetchCardData();
  }, [commander.name]);

  const handleClick = () => {
    // For double-faced cards, only use the front face name (before " // ")
    const frontFaceName = commander.name.split(' // ')[0];
    const formattedName = sanitizeCommanderName(frontFaceName);
    onClick(formattedName, frontFaceName, colors);
  };

  return (
    <div className="popular-commander-card" onClick={handleClick}>
      <div
        className={`popular-commander-image ${isLoading ? 'loading' : ''}`}
        style={{ backgroundImage: imageUrl ? `url(${imageUrl})` : 'none' }}
      />
      <div className="popular-commander-name">{commander.display}</div>
    </div>
  );
};

const App = () => {
  const [themes, setThemes] = useState([]);
  const [allCommanders, setAllCommanders] = useState([]);
  
  // Initialize currentPage and viewingDeckId from URL synchronously to avoid unnecessary fetches
  const [currentPage, setCurrentPage] = useState(() => {
    const path = window.location.pathname.toLowerCase();
    if (path.match(/^\/deck\/.+$/)) return 'viewdeck';
    if (path === '/faq') return 'faq';
    if (path === '/decks') return 'mydecks';
    if (path === '/manabase-generator') return 'manabase';
    return 'home';
  });
  const [viewingDeckId, setViewingDeckId] = useState(() => {
    const path = window.location.pathname;
    const deckMatch = path.match(/^\/deck\/(.+)$/);
    return deckMatch ? deckMatch[1] : null;
  });
  const [backgroundArt, setBackgroundArt] = useState('');
  const [commanderImageUrl, setCommanderImageUrl] = useState('');
  
  // Popular commanders list for random background selection
  const popularCommanders = [
    'Atraxa, Praetors\' Voice',
    'Yuriko, the Tiger\'s Shadow',
    'Korvold, Fae-Cursed King',
    'Edgar Markov',
    'Muldrotha, the Gravetide',
    'Prosper, Tome-Bound',
    'The Ur-Dragon',
    'Kenrith, the Returned King'
  ];

  // Fetch random commander art for home page background (only on homepage)
  React.useEffect(() => {
    // Skip background fetch if not on homepage
    if (currentPage !== 'home') return;
    // Skip if we already have background art
    if (backgroundArt) return;
    
    const fetchRandomBackground = async () => {
      // Check if we already picked a random commander this session
      let randomCommander = sessionStorage.getItem('homeBackgroundCommander');
      if (!randomCommander) {
        randomCommander = popularCommanders[Math.floor(Math.random() * popularCommanders.length)];
        sessionStorage.setItem('homeBackgroundCommander', randomCommander);
      }
      try {
        const data = await fetchCardByName(randomCommander);
        const artUrl = data.image_uris?.art_crop || data.card_faces?.[0]?.image_uris?.art_crop || '';
        setBackgroundArt(artUrl);
      } catch (err) {
        console.error('Error fetching background art:', err);
      }
    };
    fetchRandomBackground();
  }, [currentPage, backgroundArt]);
  
  // Check URL for deck ID on mount
  React.useEffect(() => {
    const path = window.location.pathname;
    const deckMatch = path.match(/^\/deck\/(.+)$/);
    if (deckMatch) {
      setViewingDeckId(deckMatch[1]);
      setCurrentPage('viewdeck');
      if (window.gtag) {
        window.gtag('event', 'page_view', {
          page_title: 'viewdeck',
          page_location: window.location.href
        });
      }
    }
  }, []);

  // Update URL when viewing deck changes
  React.useEffect(() => {
    if (viewingDeckId && currentPage === 'viewdeck') {
      window.history.pushState({}, '', `/deck/${viewingDeckId}`);
    }
  }, [viewingDeckId, currentPage]);

    // Fetch all commanders from backend on mount
    React.useEffect(() => {
      const fetchCommanders = async () => {
        try {
          const response = await fetch(`${API_URL}/v1/commanders`);
          const data = await response.json();
          setAllCommanders(data);
        } catch (err) {
          console.error('Error fetching commanders:', err);
          setAllCommanders([]);
        }
      };
      fetchCommanders();
    }, []);
  const [error, setError] = useState('');
  const [themeDetails, setThemeDetails] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState('');
  const [selectedCommander, setSelectedCommander] = useState('');
  const [selectedCommanderDisplay, setSelectedCommanderDisplay] = useState('');
  const [commanderColors, setCommanderColors] = useState([]);
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [underplayedCommanders] = useState(() => getRandomCommanders(UNDERPLAYED_COMMANDERS, 4));
  
  // Partner commander state
  const [partnerCommander, setPartnerCommander] = useState(null);
  const [showPartnerSelector, setShowPartnerSelector] = useState(false);
  const [pendingCommanderData, setPendingCommanderData] = useState(null);
  const [partnerInfo, setPartnerInfo] = useState(null);

  // Restore pending deck session after OAuth redirect
  React.useEffect(() => {
    const pendingDeck = localStorage.getItem('pendingDeckData');
    const pendingSave = localStorage.getItem('pendingSaveDeck') === 'true';
    
    if (pendingDeck && pendingSave) {
      try {
        const data = JSON.parse(pendingDeck);
        // Only restore if data is less than 24 hours old
        if (data.timestamp && Date.now() - data.timestamp < 86400000) {
          console.log('Restoring pending deck session:', data.commander);
          if (data.themeDetails) {
            setThemeDetails(data.themeDetails);
          }
          if (data.commander) {
            setSelectedCommanderDisplay(data.commander);
            setSelectedCommander(sanitizeCommanderName(data.commander));
          }
          if (data.commanderColors) {
            setCommanderColors(data.commanderColors);
          }
        }
      } catch (e) {
        console.error('Error restoring pending deck:', e);
      }
    }
  }, []);

  // Apply dark mode class to body
  React.useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [isDarkMode]);

  const fetchThemes = async (commander, displayName, colors = []) => {
    try {
      setError('');
      setThemeDetails(null);
      setPartnerCommander(null);

      // Fetch full commander data from Scryfall
      const cardData = await fetchCardByName(displayName || commander);
      const artUrl = cardData.image_uris?.art_crop || cardData.card_faces?.[0]?.image_uris?.art_crop || '';
      setBackgroundArt(artUrl);
      
      // Get full card image for display
      const cardImageUrl = cardData.image_uris?.normal || cardData.card_faces?.[0]?.image_uris?.normal || '';
      setCommanderImageUrl(cardImageUrl);
      
      // Get accurate color identity from Scryfall
      const actualColors = cardData.color_identity || colors;
      
      // Check if this commander has partner abilities
      const partnerType = getPartnerType(cardData);
      
      if (partnerType) {
        // If it's "Partner with [specific name]", auto-select that partner
        if (partnerType.type === 'partner_with' && partnerType.partnerName) {
          const partnerCardData = await fetchCardByName(partnerType.partnerName);
          const partnerArtUrl = partnerCardData.image_uris?.art_crop || 
                               partnerCardData.card_faces?.[0]?.image_uris?.art_crop || '';
          
          // Auto-select the specific partner
          const partner = {
            name: partnerCardData.name,
            colors: partnerCardData.color_identity || [],
            image: partnerArtUrl
          };
          
          // Combine color identities
          const combinedColors = [...new Set([...actualColors, ...partner.colors])];
          
          const partnerFormattedName = sanitizeCommanderName(partner.name);
          const combinedCommanderUrl = `${commander}-${partnerFormattedName}`;
          
          console.log('Auto-selected specific partner:', partnerType.partnerName);
          
          // Set partner info
          setPartnerCommander({
            name: partner.name,
            formattedName: partnerFormattedName,
            colors: partner.colors,
            image: partner.image
          });
          
          setSelectedCommander(combinedCommanderUrl);
          setSelectedCommanderDisplay(displayName || commander);
          setCommanderColors(combinedColors);
          
          // Fetch themes for the partner pair
          const response = await fetch(`${API_URL}/v1/commander/${combinedCommanderUrl}/themes`);
          const data = await response.json();
          
          if (!response.ok) {
            throw new Error(data.error || 'Failed to fetch themes');
          }
          
          const filteredThemes = data.filter(theme => theme.toLowerCase() !== 'precon');
          setThemes(filteredThemes);
          window.scrollTo(0, 0);
          return;
        }
        
        // Other partner types - show the modal
        setPendingCommanderData({
          name: displayName || commander,
          formattedName: commander,
          colors: actualColors,
          image: artUrl,
          cardData: cardData
        });
        setPartnerInfo(partnerType);
        setShowPartnerSelector(true);
        return; // Wait for partner selection
      }
      
      // No partner - continue normally
      setSelectedCommander(commander);
      setSelectedCommanderDisplay(displayName || commander);
      setCommanderColors(actualColors);
      
      const response = await fetch(`${API_URL}/v1/commander/${commander}/themes`);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch themes');
      }
      
      // Filter out "Precon" theme - not useful for deckbuilding
      const filteredThemes = data.filter(theme => theme.toLowerCase() !== 'precon');
      setThemes(filteredThemes);
      window.scrollTo(0, 0);
    } catch (err) {
      console.error('Error details:', err);
      setError(err.message);
      setThemes([]);
    }
  };

  // Handle partner selection
  const handlePartnerSelect = async (partner) => {
    if (!pendingCommanderData) return;
    
    setShowPartnerSelector(false);
    
    // Combine color identities (unique colors)
    const combinedColors = [...new Set([
      ...(pendingCommanderData.colors || []),
      ...(partner.colors || [])
    ])];
    
    const partnerFormattedName = sanitizeCommanderName(partner.name);
    
    console.log('Partner selection debug:', {
      commander: pendingCommanderData.formattedName,
      partner: partnerFormattedName
    });
    
    // Set partner info
    setPartnerCommander({
      name: partner.name,
      formattedName: partnerFormattedName,
      colors: partner.colors,
      image: partner.image
    });
    
    // Set display name and colors (commander URL will be set after we find the working one)
    setSelectedCommanderDisplay(pendingCommanderData.name);
    setCommanderColors(combinedColors);
    
    // Try fetching themes - EDHREC may list partners in either order
    // Try: commander-partner, then partner-commander, then solo commander
    try {
      const combinedUrl1 = `${pendingCommanderData.formattedName}-${partnerFormattedName}`;
      const combinedUrl2 = `${partnerFormattedName}-${pendingCommanderData.formattedName}`;
      
      console.log('Trying partner URLs:', { url1: combinedUrl1, url2: combinedUrl2 });
      
      // Try first ordering (user's order)
      let response = await fetch(`${API_URL}/v1/commander/${combinedUrl1}/themes`);
      let data = await response.json();
      let workingUrl = combinedUrl1;
      
      // If first fails, try reversed order (EDHREC might use different ordering)
      if (!response.ok) {
        console.log('First order failed, trying reversed order...');
        response = await fetch(`${API_URL}/v1/commander/${combinedUrl2}/themes`);
        data = await response.json();
        workingUrl = combinedUrl2;
      }
      
      // If both partner orderings fail, fall back to primary commander alone
      if (!response.ok) {
        console.log('Both partner orderings failed, trying single commander...');
        response = await fetch(`${API_URL}/v1/commander/${pendingCommanderData.formattedName}/themes`);
        data = await response.json();
        workingUrl = pendingCommanderData.formattedName;
        
        if (!response.ok) {
          throw new Error(data.error || 'Failed to fetch themes');
        }
      }
      
      // Use the working URL for subsequent theme card fetches
      setSelectedCommander(workingUrl);
      console.log('Using commander URL:', workingUrl);
      
      const filteredThemes = data.filter(theme => theme.toLowerCase() !== 'precon');
      console.log('Setting themes:', filteredThemes);
      setThemes(filteredThemes);
      window.scrollTo(0, 0);
    } catch (err) {
      console.error('Error fetching themes:', err);
      setError(err.message);
      setThemes([]);
    }
    
    setPendingCommanderData(null);
    setPartnerInfo(null);
  };

  // Handle "No Partner" selection
  const handleNoPartner = async () => {
    if (!pendingCommanderData) return;
    
    setShowPartnerSelector(false);
    setPartnerCommander(null);
    
    // Set main commander
    setSelectedCommander(pendingCommanderData.formattedName);
    setSelectedCommanderDisplay(pendingCommanderData.name);
    setCommanderColors(pendingCommanderData.colors);
    
    // Continue fetching themes
    try {
      const response = await fetch(`${API_URL}/v1/commander/${pendingCommanderData.formattedName}/themes`);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch themes');
      }
      
      const filteredThemes = data.filter(theme => theme.toLowerCase() !== 'precon');
      setThemes(filteredThemes);
      window.scrollTo(0, 0);
    } catch (err) {
      console.error('Error fetching themes:', err);
      setError(err.message);
      setThemes([]);
    }
    
    setPendingCommanderData(null);
    setPartnerInfo(null);
  };

  const handleThemeSelect = async (commander, theme) => {
    try {
      setError('');
      const formattedTheme = sanitizeThemeName(theme);
        
      const url = `${API_URL}/v1/commander/${commander}/themes/${formattedTheme}`;
      //console.log('Requesting URL:', url);
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }
      
      //console.log('Theme details received:', data);
      setThemeDetails(data);
      setSelectedTheme(theme);
      window.scrollTo(0, 0);
    } catch (err) {
      console.error('Detailed error:', err);
      setError(err.message);
      setThemeDetails(null);
    }
  };

  const handleBack = () => {
    setThemeDetails(null);
    setEditingDeck(null);
  };

  const handleNavigate = (page) => {
    // Map page IDs to URL paths
    const pageUrls = {
      home: '/',
      faq: '/faq',
      mydecks: '/decks',
      manabase: '/manabase-generator'
    };
    
    if (page === 'home') {
      // Reset to home state
      setThemeDetails(null);
      setThemes([]);
      setSelectedCommander('');
      setSelectedCommanderDisplay('');
      setCommanderColors([]);
      setError('');
      setPartnerCommander(null);
      setShowPartnerSelector(false);
      setPendingCommanderData(null);
      setPartnerInfo(null);
      setViewingDeckId(null);
      setEditingDeck(null);
    }
    
    // Update URL
    const url = pageUrls[page] || '/';
    window.history.pushState({}, '', url);
    
    setCurrentPage(page);
    
    // Track page view in Google Analytics
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: page,
        page_location: window.location.origin + url
      });
    }
  };

  const handleViewDeck = (deckId) => {
    setViewingDeckId(deckId);
    setCurrentPage('viewdeck');
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: 'viewdeck',
        page_location: window.location.origin + '/deck/' + deckId
      });
    }
  };

  const handleBackFromDeck = () => {
    setViewingDeckId(null);
    setCurrentPage('mydecks');
    window.history.pushState({}, '', '/');
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: 'mydecks',
        page_location: window.location.origin + '/mydecks'
      });
    }
  };

  const [editingDeck, setEditingDeck] = useState(null);

  const handleEditDeck = async (deck) => {
    // Store the deck being edited
    setEditingDeck(deck);
    // Set commander info
    setSelectedCommanderDisplay(deck.commander);
    setSelectedCommander(sanitizeCommanderName(deck.commander));
    
    // Fetch commander colors from Scryfall
    try {
      const cardData = await fetchCardByName(deck.commander);
      setCommanderColors(cardData.color_identity || []);
    } catch (err) {
      console.error('Error fetching commander colors:', err);
      setCommanderColors([]);
    }
    
    // Set a minimal themeDetails to trigger CardSelector view
    setThemeDetails({ editing: true, deckId: deck.id, deckName: deck.name });
    setViewingDeckId(null);
    setCurrentPage('home');
    window.history.pushState({}, '', '/');
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: 'deck_editor',
        page_location: window.location.origin + '/edit-deck'
      });
    }
  };

  // Determine current stage for transitions
  const getCurrentStage = () => {
    if (currentPage === 'faq') return 'faq';
    if (currentPage === 'mydecks') return 'mydecks';
    if (currentPage === 'viewdeck') return 'viewdeck';
    if (currentPage === 'manabase') return 'manabase';
    if (themeDetails) return 'cards';
    if (themes.length > 0) return 'themes';
    return 'home';
  };

  const renderContent = () => {
    // View Saved Deck
    if (currentPage === 'viewdeck' && viewingDeckId) {
      return (
        <PageTransition stage="viewdeck">
          <Suspense fallback={<LoadingSpinner />}>
            <SavedDeckViewer 
              deckId={viewingDeckId} 
              onBack={handleBackFromDeck} 
              onEdit={handleEditDeck}
            />
          </Suspense>
        </PageTransition>
      );
    }

    // FAQ Page
    if (currentPage === 'faq') {
      return (
        <PageTransition stage="faq">
          <Suspense fallback={<LoadingSpinner />}>
            <FAQ />
          </Suspense>
        </PageTransition>
      );
    }

    // My Decks Page
    if (currentPage === 'mydecks') {
      return (
        <PageTransition stage="mydecks">
          <Suspense fallback={<LoadingSpinner />}>
            <MyDecks onViewDeck={handleViewDeck} onEditDeck={handleEditDeck} />
          </Suspense>
        </PageTransition>
      );
    }

    // Manabase Generator Page
    if (currentPage === 'manabase') {
      return (
        <PageTransition stage="manabase">
          <Suspense fallback={<LoadingSpinner />}>
            <ManabaseGenerator />
          </Suspense>
        </PageTransition>
      );
    }

    // Card Selector (when a theme is selected)
    if (themeDetails) {
      const displayName = partnerCommander 
        ? `${selectedCommanderDisplay} & ${partnerCommander.name}`
        : (selectedCommanderDisplay || selectedCommander);
      
      return (
        <PageTransition stage="cards">
          <div className="breadcrumb">
            <button onClick={handleBack} className="breadcrumb-link">
              ← {displayName}
            </button>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">{editingDeck ? 'Editing Deck' : 'Card Selection'}</span>
          </div>
          <Suspense fallback={<LoadingSpinner />}>
            <CardSelector 
              themeDetails={themeDetails} 
              commanderColors={commanderColors}
              commanderName={displayName}
              partnerCommander={partnerCommander}
              editingDeck={editingDeck}
              commanderImageUrl={commanderImageUrl}
              themeName={selectedTheme}
            />
          </Suspense>
        </PageTransition>
      );
    }

    // Theme List (when commander is selected)
    if (themes.length > 0) {
      const displayName = partnerCommander 
        ? `${selectedCommanderDisplay} & ${partnerCommander.name}`
        : (selectedCommanderDisplay || selectedCommander);
      
      return (
        <PageTransition stage="themes">
          <div className="commander-header">
            <h2 className="commander-title">
              Building: <span className="commander-name-highlight">{displayName}</span>
            </h2>
            {partnerCommander && (
              <div className="partner-indicator">
                <span className="partner-badge">Partner Commanders</span>
              </div>
            )}
            <p className="commander-subtitle">Choose a theme to start selecting cards</p>
          </div>
          <ThemeList 
            themes={themes} 
            commander={selectedCommander}
            onThemeSelect={handleThemeSelect}
            commanders={allCommanders}
            commanderName={displayName}
            commanderImageUrl={commanderImageUrl}
            partnerCommander={partnerCommander}
          />
        </PageTransition>
      );
    }

    // Home Page
    return (
      <PageTransition stage="home">
        <div className="hero-section">
          <h1 className="hero-title">EDH.Build | The Ultimate EDH Deck Builder</h1>
          <p className="hero-tagline">From Commander to Complete Deck in Minutes</p>
          <p className="hero-attribution">Powered by <a href="https://edhrec.com" target="_blank" rel="noopener noreferrer">EDHREC</a> data</p>
        </div>
        
        <CommanderInput onSubmit={fetchThemes} commanders={allCommanders} />
        
        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
          </div>
        )}

        <div className="how-it-works">
          <h2>How It Works</h2>
          <div className="steps">
            <div className="step">
              <div className="step-number">1</div>
              <h3>Choose Commander</h3>
              <p>Search for your commander using the search bar above</p>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <h3>Pick a Theme</h3>
              <p>Select from popular build strategies for your commander</p>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <h3>Select Cards</h3>
              <p>Choose cards from curated suggestions based on synergy</p>
            </div>
          </div>
        </div>

        <div className="popular-commanders">
          <h2>Popular Commanders</h2>
          <p className="popular-commanders-subtitle">Looking for inspiration? Try one of these fan favorites</p>
          
          {/* Underplayed - Hidden gems from the bottom of the rankings */}
          <div className="commander-row">
            <h3 className="commander-row-label">Underplayed</h3>
            <div className="popular-commanders-grid">
              {underplayedCommanders.map((commander) => (
                <PopularCommanderCard
                  key={commander.name}
                  commander={commander}
                  onClick={fetchThemes}
                />
              ))}
            </div>
          </div>

          {/* Popular - Top commanders by play rate */}
          <div className="commander-row">
            <h3 className="commander-row-label">Popular</h3>
            <div className="popular-commanders-grid">
              {[
                { name: 'Atraxa, Praetors\' Voice', display: 'Atraxa, Praetors\' Voice' },
                { name: 'Edgar Markov', display: 'Edgar Markov' },
                { name: 'Krenko, Mob Boss', display: 'Krenko, Mob Boss' },
                { name: 'Yuriko, the Tiger\'s Shadow', display: 'Yuriko, the Tiger\'s Shadow' }
              ].map((commander) => (
                <PopularCommanderCard
                  key={commander.name}
                  commander={commander}
                  onClick={fetchThemes}
                />
              ))}
            </div>
          </div>

          {/* Developer's Choice - Personal favorites */}
          <div className="commander-row">
            <h3 className="commander-row-label">Developer's Choice</h3>
            <div className="popular-commanders-grid">
              {[
                { name: 'Tasigur, the Golden Fang', display: 'Tasigur, the Golden Fang' },
                { name: 'Kenrith, the Returned King', display: 'Kenrith, the Returned King' },
                { name: 'Purphoros, God of the Forge', display: 'Purphoros, God of the Forge' },
                { name: 'Marwyn, the Nurturer', display: 'Marwyn, the Nurturer' }
              ].map((commander) => (
                <PopularCommanderCard
                  key={commander.name}
                  commander={commander}
                  onClick={fetchThemes}
                />
              ))}
            </div>
          </div>
        </div>
      </PageTransition>
    );
  };

  // Get page meta based on current page
  const getPageMeta = () => {
    switch (currentPage) {
      case 'faq':
        return { title: 'FAQ | EDH.Build' };
      case 'mydecks':
        return { title: 'My Decks | EDH.Build' };
      case 'viewdeck':
        return { title: 'View Deck | EDH.Build' };
      case 'manabase':
        return { 
          title: 'Commander Manabase Generator | EDH.Build',
          description: 'Free Commander manabase generator for MTG EDH decks. Paste your decklist and instantly generate an optimized manabase based on your color requirements, pip counts, and budget. Smart EDH manabase builder with fetchland balancing and basic land distribution.',
          keywords: 'manabase generator, commander manabase generator, EDH manabase generator, MTG manabase, commander land base, EDH land base, magic the gathering manabase, commander deck lands, EDH deck builder'
        };
      default:
        return { title: 'EDH.Build - Smart Commander Deck Builder | Data-Driven EDH Recommendations' };
    }
  };

  const pageMeta = getPageMeta();

  return (
    <div className={`App ${isDarkMode ? 'dark-mode' : ''}`}>
      {/* Page-specific meta tags */}
      <Helmet>
        <title>{pageMeta.title}</title>
        {pageMeta.description && <meta name="description" content={pageMeta.description} />}
        {pageMeta.keywords && <meta name="keywords" content={pageMeta.keywords} />}
      </Helmet>
      
      {/* Background art layer */}
      {backgroundArt && (
        <div 
          className="background-art"
          style={{ backgroundImage: `url(${backgroundArt})` }}
        />
      )}
      
      {/* Partner Selector Modal */}
      {showPartnerSelector && pendingCommanderData && partnerInfo && (
        <PartnerSelector
          commanderData={pendingCommanderData}
          partnerInfo={partnerInfo}
          onPartnerSelect={handlePartnerSelect}
          onNoPartner={handleNoPartner}
        />
      )}
      
      <Header 
        currentPage={currentPage}
        onNavigate={handleNavigate}
        isDarkMode={isDarkMode}
        onToggleDarkMode={() => setIsDarkMode(!isDarkMode)}
      />
      
      <main className="main-container">
        {renderContent()}
      </main>

      <Footer />
    </div>
  );
};

export default App;
